import { useState } from 'react'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { ChevronRight, Download } from 'lucide-react'
import { usePageHeader } from '@/components/page-header-context'
import { Avatar } from '@/components/ui/avatar'
import { Pill } from '@/components/ui/pill'
import { Button } from '@/components/ui/button'
import { formatDt } from '@/features/dashboard/format'
import {
  parseFilters,
  useExportRecovery,
  useRecoveryDrivers,
} from '@/features/billing/use-recovery'
import {
  ACCOUNT_STATUS_META,
  PAGE_SIZES,
  STATUS_OPTIONS,
  type LedgerStatusFilter,
  type RecoveryDriverIdentity,
  type RecoveryFilters,
} from '@/features/billing/recovery.types'
import RecoveryDriverDrawer from './RecoveryDriverDrawer'

const COLS = 'grid grid-cols-[2.2fr_1fr_1.2fr_1.4fr_1.1fr_0.8fr] gap-4'

export default function RecoveryPage() {
  usePageHeader('Recouvrement', 'Soldes, factures et relance des chauffeurs en retard')
  const navigate = useNavigate()
  const search = useSearch({ strict: false }) as Record<string, unknown>
  const filters = parseFilters(search)
  const [selected, setSelected] = useState<RecoveryDriverIdentity | null>(null)

  const { data, isLoading, isError, isFetching } = useRecoveryDrivers(filters)
  const exportCsv = useExportRecovery()
  const rows = data?.data ?? []
  const total = data?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / filters.limit))

  /** Met à jour l'URL (et donc les filtres). Tout changement de filtre repart page 1. */
  const setFilters = (patch: Partial<RecoveryFilters>) => {
    navigate({
      to: '/billing',
      search: (prev: Record<string, unknown>) => {
        const next: Record<string, unknown> = { ...prev, ...patch }
        if (!('page' in patch)) next.page = 1
        for (const key of Object.keys(next)) {
          if (next[key] === '' || next[key] === undefined || next[key] === false) delete next[key]
        }
        return next
      },
    })
  }

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Barre de filtres */}
      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1">
          <span className="text-[11px] font-bold uppercase tracking-wide text-muted">Statut</span>
          <select
            aria-label="Filtrer par statut"
            value={filters.status ?? ''}
            onChange={(e) =>
              setFilters({ status: (e.target.value || undefined) as LedgerStatusFilter })
            }
            className="h-9 rounded-lg border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="">Tous</option>
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>

        <label className="flex items-center gap-2 h-9 px-3 rounded-lg border border-gray-300 cursor-pointer">
          <input
            type="checkbox"
            aria-label="En retard uniquement"
            checked={!!filters.overdue}
            onChange={(e) => setFilters({ overdue: e.target.checked || undefined })}
          />
          <span className="text-sm text-ink-soft">En retard</span>
        </label>

        <div className="flex-1" />

        <Button
          type="button"
          variant="outline"
          className="h-9 gap-2"
          disabled={exportCsv.isPending || total === 0}
          onClick={() => exportCsv.mutate(filters)}
        >
          <Download size={15} strokeWidth={2.2} />
          {exportCsv.isPending ? 'Export…' : 'Exporter CSV'}
        </Button>
      </div>

      {exportCsv.isError && (
        <p className="mt-2 text-xs text-danger">Échec de l'export — réessayez.</p>
      )}

      {/* Tableau */}
      <div className="mt-4 bg-surface rounded-[18px] border border-line overflow-hidden flex-1 flex flex-col min-h-0">
        <div className={`${COLS} px-6 py-3.5 border-b border-line bg-surfaceAlt`}>
          {['Chauffeur', 'Dette', 'Statut', 'Dernière facture', 'Échéance', ''].map((h, i) => (
            <span
              key={h || 'action'}
              className={`text-[11px] font-bold tracking-wide uppercase text-muted ${i === 5 ? 'text-right' : ''}`}
            >
              {h}
            </span>
          ))}
        </div>

        <div className="flex-1 overflow-auto">
          {isLoading && <p className="p-6 text-muted">Chargement…</p>}
          {isError && <p className="p-6 text-danger">Erreur lors du chargement du recouvrement.</p>}
          {!isLoading && !isError && rows.length === 0 && (
            <p className="p-8 text-center text-muted">Aucun chauffeur à recouvrer.</p>
          )}
          {!isLoading &&
            !isError &&
            rows.map((r) => {
              const name = `${r.driver.firstName} ${r.driver.lastName}`.trim() || '—'
              const meta = ACCOUNT_STATUS_META[r.accountStatus]
              return (
                <div
                  key={r.driver.id}
                  className={`${COLS} items-center px-6 py-3.5 border-b border-line last:border-0`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <Avatar name={name} size={38} />
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-ink truncate">{name}</div>
                      <div className="font-mono text-[11.5px] text-muted mt-0.5">
                        {r.driver.phone ?? '—'}
                      </div>
                    </div>
                  </div>
                  <span className="text-[13px] font-semibold text-ink">
                    {formatDt(r.balanceDue)}
                  </span>
                  <div>
                    <Pill tone={meta.tone} mono className="!px-2.5 !py-1 text-[11px]">
                      {meta.label}
                    </Pill>
                  </div>
                  <span className="text-[13px] text-ink-soft">
                    {r.lastInvoice ? formatDt(r.lastInvoice.totalAmount) : '—'}
                    {r.overdueAmount > 0 && (
                      <span className="text-danger"> · {formatDt(r.overdueAmount)} dû</span>
                    )}
                  </span>
                  <span className="text-[13px] text-ink-soft">
                    {r.lastInvoice
                      ? new Date(r.lastInvoice.dueDate).toLocaleDateString('fr-TN')
                      : '—'}
                  </span>
                  <div className="flex justify-end">
                    <button
                      onClick={() => setSelected(r.driver)}
                      className="h-[34px] px-4 rounded-[10px] flex items-center gap-1.5 text-[12.5px] font-semibold bg-surfaceAlt text-ink hover:bg-line"
                    >
                      Fiche
                      <ChevronRight size={14} strokeWidth={2.4} />
                    </button>
                  </div>
                </div>
              )
            })}
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between gap-4 px-6 py-3 border-t border-line bg-surfaceAlt">
          <div className="flex items-center gap-3">
            <span className="text-[12.5px] text-muted">
              {total} chauffeur{total > 1 ? 's' : ''}
              {isFetching && ' · …'}
            </span>
            <select
              aria-label="Lignes par page"
              value={filters.limit}
              onChange={(e) => setFilters({ limit: Number(e.target.value) })}
              className="h-8 rounded-lg border border-gray-300 px-2 text-[12.5px] focus:outline-none focus:ring-2 focus:ring-primary"
            >
              {PAGE_SIZES.map((size) => (
                <option key={size} value={size}>
                  {size} / page
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={filters.page <= 1}
              onClick={() => setFilters({ page: filters.page - 1 })}
            >
              Précédent
            </Button>
            <span className="text-[12.5px] text-ink-soft">
              {filters.page} / {totalPages}
            </span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={filters.page >= totalPages}
              onClick={() => setFilters({ page: filters.page + 1 })}
            >
              Suivant
            </Button>
          </div>
        </div>
      </div>

      {selected && <RecoveryDriverDrawer driver={selected} onClose={() => setSelected(null)} />}
    </div>
  )
}
